import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { SiteChip } from '@/settings/SiteChip'
import { ApprovalsForm } from '@/settings/approvals/form'
import type { GatingBody } from '@/settings/approvals/form'
import { summariseGating } from '@/settings/approvals/summary'
import { useSettingsSite } from '@/settings/site'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { useOneEditing } from '@/ui/useOneEditing'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Settings › SEO › Approvals — what AugRank may change on a site without
 * asking, per site.
 *
 * Settings consolidation (2026-09-15). Was `/seo/approvals/gating`, which now
 * redirects here. One `GET` answers for every site; the chip picks which row
 * is shown. The form is `settings/approvals/form.tsx`, shared with the wizard.
 */
export function ApprovalsPanel(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  const editable = may(membership, 'tenant:update')
  const site = useSettingsSite()
  const one = useOneEditing()
  const toast = useToast()
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const gating = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/approvals/sites/gating', { signal })),
    [],
  )
  const row =
    (gating.state.data?.sites ?? []).find((entry) => entry.site_id === site.site?.id) ?? null

  const save = useMutation((body: { site_id: string; gating: GatingBody }) =>
    call(() =>
      api.PUT('/api/v1/seo/approvals/sites/{site_id}/gating', {
        params: { path: { site_id: body.site_id } },
        body: body.gating,
      }),
    ),
  )

  return (
    <div className="ar-stack">
      <div className="ar-row">
        <h2 className="ar-h2">Approvals</h2>
        <span className="ar-card__spacer" />
        <SiteChip site={site} />
      </div>

      {site.status === 'loading' || gating.state.status === 'loading' ? (
        <Loading label="Loading your sites" lines={2} />
      ) : null}
      {site.status === 'error' && site.failure !== null ? (
        <ErrorState failure={site.failure} onRetry={site.retry} />
      ) : null}
      {gating.state.status === 'error' ? (
        <ErrorState failure={gating.state.failure} onRetry={gating.refetch} />
      ) : null}
      {site.status === 'ready' && site.site === null ? (
        <Empty
          title="No site yet"
          body={`Add your website and you can decide what ${brand.productName} may change on it without asking.`}
          action={
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/seo/sites">
              Go to Sites
            </Link>
          }
        />
      ) : null}

      {site.site !== null && row !== null ? (
        <SettingRow
          key={row.site_id}
          label="Approvals"
          summary={summariseGating(row)}
          savedAt={savedAt}
          editable={editable}
          editing={one.editing === 'gating'}
          onEdit={() => {
            one.open('gating')
          }}
          onCancel={one.close}
        >
          <ApprovalsForm
            site={row}
            submitLabel="Save"
            onSubmit={async (body) => {
              const result = await save.run({ site_id: row.site_id, gating: body })
              if (result.ok) {
                setSavedAt(new Date().toISOString())
                one.close()
                // Re-read rather than trust the press: the answer is what was
                // stored, and an unrecognised value is dropped by the server.
                gating.refetch()
                toast.show({
                  kind: 'success',
                  title: `Saved for ${row.domain}`,
                  body: 'It applies to the next run. Anything already waiting stays waiting.',
                })
              }
              return result
            }}
          />
        </SettingRow>
      ) : null}
    </div>
  )
}
